import type { ReactNode } from 'react';
import { ChevronRight } from '@retail-ops/panel-shell/fi';

export interface DataGridColumn<R> {
  key: string;
  label: string;
  numeric?: boolean;
  render: (row: R) => ReactNode;
  width?: string;
}

export interface DataGridProps<R> {
  columns: DataGridColumn<R>[];
  rows: R[];
  rowKey: (row: R) => string;
  onRowAction?: (row: R) => void;
  rowAddedPredicate?: (row: R) => boolean;
  emptyMessage?: string;
}

/** Primitive 9: Data grid (nCino-style). */
export function DataGrid<R>({
  columns,
  rows,
  rowKey,
  onRowAction,
  rowAddedPredicate,
  emptyMessage = 'No rows to display.',
}: DataGridProps<R>): JSX.Element {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-10)',
          background: 'var(--ink-50)',
          border: '1px dashed var(--ink-300)',
          borderRadius: 'var(--r-md)',
          color: 'var(--ink-600)',
          fontSize: 13,
          textAlign: 'center',
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <table className="grid-table">
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              style={{ textAlign: c.numeric ? 'right' : 'left', width: c.width }}
            >
              {c.label}
            </th>
          ))}
          {onRowAction && <th style={{ width: 36 }} aria-label="row action" />}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const added = rowAddedPredicate?.(r) === true;
          return (
            <tr key={rowKey(r)} className={added ? 'row-added' : ''}>
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? 'numeric' : ''}>
                  {c.render(r)}
                </td>
              ))}
              {onRowAction && (
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-link"
                    aria-label="Open row"
                    onClick={() => onRowAction(r)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
